import { useNavigate, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import React, { useEffect, useState, useMemo } from "react";

import type { AppDispatch, RootState } from "../../store";
import { toggleDarkMode } from "../../store/theme/themeSlice";
import { changeLanguage } from "../../store/language/languageSlice";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { darkMode } = useSelector((state: RootState) => state.theme);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage);
  }, [currentLanguage, i18n]);

  // When language changes, update URL
  const handleLanguageChange = (newLang: string) => {
    dispatch(changeLanguage(newLang));
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, "");
    const newPath = `/${newLang}${pathWithoutLang}`;
    navigate(`${newPath}${currentSearch}`, { replace: true });
    setIsLanguageMenuOpen(false);
  };

  const handleThemeToggle = () => {
    dispatch(toggleDarkMode());
  };

  const { t } = useTranslation();

  // Memoized data for better performance
  const languages = useMemo(
    () => [
      {
        code: "en",
        name: t("header.language") === "Language" ? "English" : "English",
        flag: "🇺🇸",
      },
      {
        code: "fr",
        name: t("header.language") === "Language" ? "Français" : "Français",
        flag: "🇫🇷",
      },
    ],
    [t]
  );

  const navItems = useMemo(
    () => [
      { href: `/${currentLanguage}`, label: t("header.nav.home"), key: "home" },
      {
        href: `/${currentLanguage}/about`,
        label: t("header.nav.about"),
        key: "about",
      },
      {
        href: `/${currentLanguage}/projects`,
        label: t("header.nav.projects"),
        key: "projects",
      },
      {
        href: `/${currentLanguage}/attractions`,
        label: t("attractions.title", "Attractions"),
        key: "attractions",
      },
      {
        href: `/${currentLanguage}/businesses`,
        label: t("header.nav.businesses"),
        key: "businesses",
      },
      {
        href: `/${currentLanguage}/events`,
        label: t("header.nav.events"),
        key: "events",
      },
      {
        href: `/${currentLanguage}/scholarships`,
        label: t("header.nav.scholarships"),
        key: "scholarships",
      },
      {
        href: `/${currentLanguage}/blogs`,
        label: t("header.nav.blog"),
        key: "blog",
      },
    ],
    [t, currentLanguage]
  );

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="sticky top-0 z-20 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-700"
    >
      <div className="max-w-full mx-auto px-4 lg:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Left Section - Logo */}
          <div className="flex items-center gap-3">
            <img
              src="/assets/ddc-logo.jpeg"
              alt="DDC Logo"
              className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
            />
            <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
              DDC
            </h2>
          </div>

          {/* Center Section - Navigation (Desktop) */}
          <nav className="hidden lg:flex items-center justify-center flex-1 px-8">
            <div className="flex items-center gap-6 lg:gap-8">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="text-sm lg:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors duration-200"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </nav>

          {/* Right Section - Controls */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Dropdown */}
            <div className="relative">
              <button
                id="languageButton"
                onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                className="p-2 flex items-center gap-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 rounded-lg"
                aria-haspopup="true"
                aria-expanded={isLanguageMenuOpen}
              >
                <span>
                  {languages.find((lang) => lang.code === currentLanguage)
                    ?.flag || "🌐"}
                </span>
                <span className="hidden lg:inline text-sm lg:text-base">
                  {languages.find((lang) => lang.code === currentLanguage)
                    ?.name || t("header.language")}
                </span>
              </button>

              {isLanguageMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute lg:right-0 left-1/2 lg:left-auto transform -translate-x-1/2 lg:transform-none mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
                >
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 items-center gap-2 first:rounded-t-md last:rounded-b-md"
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </div>

            {/* Theme Toggle */}
            <button
              onClick={handleThemeToggle}
              className="p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 rounded-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20px"
                height="20px"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M233.54,142.23a8,8,0,0,0-8-2,88.08,88.08,0,0,1-109.8-109.8,8,8,0,0,0-10-10,104.84,104.84,0,0,0-52.91,37A104,104,0,0,0,136,224a103.09,103.09,0,0,0,62.52-20.88,104.84,104.84,0,0,0,37-52.91A8,8,0,0,0,233.54,142.23ZM188.9,190.34A88,88,0,0,1,65.66,67.11a89,89,0,0,1,31.4-26A106,106,0,0,0,96,56,104.11,104.11,0,0,0,200,160a106,106,0,0,0,14.92-1.06A89,89,0,0,1,188.9,190.34Z" />
              </svg>
            </button>

            {/* Auth Buttons */}
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {/* <span className="text-sm font-medium text-gray-700 dark:text-gray-300 hidden lg:block">
                  {t("header.welcome", "Welcome")}, {user?.name}
                </span> */}
                <Link
                  to={`/${currentLanguage}/admin/dashboard`}
                  className="bg-gradient-to-r from-emerald-600 to-stone-600 text-white font-semibold rounded-lg px-4 py-2 hover:from-emerald-700 hover:to-stone-700 dark:from-emerald-700 dark:to-stone-700 dark:hover:from-emerald-600 dark:hover:to-stone-600 transition-all duration-200 text-sm lg:text-base shadow-md hover:shadow-lg"
                >
                  {t("header.dashboard", "Dashboard")}
                </Link>
              </div>
            ) : (
              <Link
                to={`/${currentLanguage}/auth/login`}
                className="bg-gradient-to-r from-emerald-600 to-stone-600 text-white font-semibold rounded-lg px-4 py-2 hover:from-emerald-700 hover:to-stone-700 dark:from-emerald-700 dark:to-stone-700 dark:hover:from-emerald-600 dark:hover:to-stone-600 transition-all duration-200 text-sm lg:text-base shadow-md hover:shadow-lg"
              >
                {t("header.login", "Login")}
              </Link>
            )}

            {/* Donate Button */}
            {/* <button className="bg-emerald-600 text-white font-bold rounded-lg px-3 py-2 hover:bg-emerald-800 dark:bg-emerald-700 dark:hover:bg-emerald-600 transition-colors duration-200 text-sm lg:text-base">
              {t("header.donateNow")}
            </button> */}

            {/* Mobile Menu Button */}
            {/* <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 rounded-lg"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20px"
                height="20px"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128ZM40,72H216a8,8,0,0,0,0-16H40a8,8,0,0,0,0,16ZM216,184H40a8,8,0,0,0,0,16H216a8,8,0,0,0,0-16Z" />
              </svg>
            </button> */}
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                onClick={() => setIsMobileMenuOpen(false)}
              />

              {/* Centered Menu */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="fixed inset-0 flex items-center justify-center z-50 lg:hidden"
              >
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-11/12 max-w-sm mx-4">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Menu
                      </h3>
                      <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      >
                        <svg
                          className="w-5 h-5 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="space-y-2">
                      {navItems.map((item, index) => (
                        <motion.div
                          key={item.key}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Link
                            to={item.href}
                            className="block px-4 py-3 text-base font-medium text-gray-700 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

// Display name for debugging
Header.displayName = "Header";

export default Header;
